import { useState, useEffect } from "react";
import { toast } from "sonner";
import { X, Sparkle, ClipboardText, ArrowRight, CheckCircle, Warning, EnvelopeSimple, PaperPlaneTilt, TrendUp, Gauge, Path, Camera } from "@phosphor-icons/react";
import client from "../lib/api";
import PastedLoadRiskWarning from "../components/PastedLoadRiskWarning";

const EXAMPLE_TEXT = `Origin: Dallas, TX 75201
Destination: Atlanta, GA 30303
Pickup: 07/25/2026 08:00
Delivery: 07/26/2026 by 17:00
Equipment: Dry Van 53ft
Weight: 42,000 lbs
Rate: $2,450 all-in
Broker: TQL — Total Quality Logistics
Ref #: TQL-882741`;

/**
 * PasteLoadModal — AI-powered load parser.
 * Copy-paste a load posting from DAT, Truckstop, an email, or SMS.
 * AI extracts the fields; user reviews and saves.
 */
export default function PasteLoadModal({ open, onClose, onCreated }) {
  const [step, setStep] = useState("paste"); // paste | review | negotiate
  const [text, setText] = useState("");
  const [parsing, setParsing] = useState(false);
  const [parsed, setParsed] = useState(null);
  const [saving, setSaving] = useState(false);

  // Negotiation state
  const [brokerEmail, setBrokerEmail] = useState("");
  const [brokerName, setBrokerName] = useState("");
  const [reasons, setReasons] = useState(["fuel", "deadhead", "market"]);
  const [drafting, setDrafting] = useState(false);
  const [draft, setDraft] = useState(null); // { subject, body, target_rate, anchor_rate, uplift_applied, fuel, deadhead }
  const [sending, setSending] = useState(false);
  const [savedLoadId, setSavedLoadId] = useState(null);

  // Live signals — fetched lazily when the negotiate step opens
  const [fuel, setFuel] = useState(null); // {price_per_gallon, wow_change_usd, wow_change_pct, as_of, source}
  const [deadhead, setDeadhead] = useState(null); // {miles, from, to, prior_load_number} | {miles:0, no_prior_load:true}

  // Fetch fuel + deadhead as soon as the negotiate step is reached (or refetch on origin change)
  useEffect(() => {
    if (step !== "negotiate" || !parsed) return;
    let cancelled = false;
    (async () => {
      try {
        const [fRes, dRes] = await Promise.all([
          client.get("/dispatch/fuel-price").catch(() => null),
          parsed.origin
            ? client.post("/dispatch/deadhead", { next_origin: parsed.origin }).catch(() => null)
            : Promise.resolve(null),
        ]);
        if (cancelled) return;
        if (fRes?.data) setFuel(fRes.data);
        if (dRes?.data) setDeadhead(dRes.data);
      } catch {
        /* silent — badges are optional */
      }
    })();
    return () => { cancelled = true; };
    // Only re-fetch when the pilot enters the negotiate step or changes origin.
    // Full `parsed` object identity churns as the user types other fields — excluded intentionally.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step, parsed?.origin]);

  const REASONS = [
    { key: "fuel", label: "⛽ Fuel prices up" },
    { key: "deadhead", label: "📏 Deadhead / empty return" },
    { key: "market", label: "📈 Market rate above post" },
    { key: "urgency", label: "⏰ Tight pickup window" },
    { key: "equipment", label: "🚛 Specialized equipment" },
    { key: "weather", label: "🌦️ Weather / route conditions" },
    { key: "hos", label: "🕐 Tight HOS math" },
  ];

  if (!open) return null;

  async function handleParse() {
    if (text.trim().length < 10) {
      toast.error("Paste some load text first");
      return;
    }
    setParsing(true);
    try {
      const { data } = await client.post("/dispatch/parse-load-text", { text });
      setParsed({
        load_number: data.load_number || "",
        customer: data.customer || "",
        origin: data.origin || "",
        destination: data.destination || "",
        pickup_date: data.pickup_date || "",
        delivery_date: data.delivery_date || "",
        rate: data.rate || 0,
        notes: data.notes || "",
        confidence: data.confidence ?? 0.7,
        status: "pending",
      });
      setStep("review");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "AI couldn't parse this. Try cleaning up the text.");
    } finally {
      setParsing(false);
    }
  }

  async function handleScreenshot(e) {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (file.size > 8 * 1024 * 1024) {
      toast.error("Screenshot too big — crop it to just the load card (max 8MB)");
      return;
    }
    setParsing(true);
    try {
      const dataUrl = await new Promise((res, rej) => {
        const r = new FileReader();
        r.onload = () => res(r.result);
        r.onerror = rej;
        r.readAsDataURL(file);
      });
      const { data } = await client.post("/dispatch/parse-load-image", { image_base64: dataUrl });
      setParsed({
        load_number: data.load_number || "",
        customer: data.customer || "",
        origin: data.origin || "",
        destination: data.destination || "",
        pickup_date: data.pickup_date || "",
        delivery_date: data.delivery_date || "",
        rate: data.rate || 0,
        notes: data.notes || "",
        confidence: data.confidence ?? 0.7,
        status: "pending",
      });
      setStep("review");
      toast.success("Screenshot read ✓ — review the fields below");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "AI couldn't read that screenshot. Try a clearer, closer shot of the load.");
    } finally {
      setParsing(false);
    }
  }

  async function handleSave() {
    // Basic validation before hitting server
    if (!parsed.load_number || !parsed.customer || !parsed.origin || !parsed.destination || !parsed.pickup_date || !parsed.delivery_date) {
      toast.error("Fill in missing required fields");
      return;
    }
    setSaving(true);
    try {
      const payload = { ...parsed };
      delete payload.confidence;
      const { data } = await client.post("/loads", payload);
      setSavedLoadId(data.id);
      toast.success(`✓ Load ${parsed.load_number} saved to dispatch board`);
      // Move to negotiate step instead of closing
      setStep("negotiate");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't save load. Try again.");
    } finally {
      setSaving(false);
    }
  }

  async function handleDraftEmail() {
    if (!brokerEmail || !brokerEmail.includes("@")) {
      toast.error("Enter a valid broker email");
      return;
    }
    if (!parsed.rate || parsed.rate <= 0) {
      toast.error("Rate is required to negotiate");
      return;
    }
    setDrafting(true);
    try {
      const { data } = await client.post("/dispatch/negotiate-rate", {
        load_number: parsed.load_number,
        customer: parsed.customer,
        origin: parsed.origin,
        destination: parsed.destination,
        pickup_date: parsed.pickup_date,
        delivery_date: parsed.delivery_date,
        posted_rate: Number(parsed.rate) || 0,
        notes: parsed.notes || "",
        reasons,
        broker_name: brokerName || "",
        broker_email: brokerEmail,
      });
      setDraft(data);
      // Sync the badge state to what the server actually used for the draft
      if (data.fuel) setFuel(data.fuel);
      if (data.deadhead !== undefined) setDeadhead(data.deadhead);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't draft email");
    } finally {
      setDrafting(false);
    }
  }

  async function handleSendEmail() {
    if (!draft) return;
    setSending(true);
    try {
      await client.post("/dispatch/send-broker-email", {
        to_email: brokerEmail,
        subject: draft.subject,
        body: draft.body,
        load_id: savedLoadId,
        load_number: parsed.load_number,
      });
      toast.success(`✓ Email sent to ${brokerEmail}. Target: $${draft.target_rate.toLocaleString()}`);
      resetAndClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Send failed");
    } finally {
      setSending(false);
    }
  }

  function resetAndClose() {
    setText("");
    setParsed(null);
    setStep("paste");
    setBrokerEmail("");
    setBrokerName("");
    setReasons(["fuel", "deadhead", "market"]);
    setDraft(null);
    setSavedLoadId(null);
    setFuel(null);
    setDeadhead(null);
    onCreated?.();
    onClose();
  }

  function close() {
    if (parsing || saving || drafting || sending) return;
    // If they saved a load but haven't sent an email, still refresh the dispatch list
    if (savedLoadId) onCreated?.();
    resetAndClose();
  }

  const confidence = parsed?.confidence ?? 0;
  const confPct = Math.round(confidence * 100);
  const confColor = confPct >= 85 ? "text-emerald-400" : confPct >= 65 ? "text-amber-400" : "text-red-400";

  return (
    <div
      className="fixed inset-0 bg-black/70 backdrop-blur-sm z-[60] flex items-center justify-center p-4"
      onClick={close}
      data-testid="paste-load-modal"
    >
      <div
        className="bg-[#0f1116] border border-white/10 rounded-2xl w-full max-w-3xl max-h-[90vh] overflow-hidden shadow-2xl flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-white/10">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-[#FF5A1F]/15 text-[#FF5A1F] flex items-center justify-center">
              <ClipboardText size={20} weight="fill" />
            </div>
            <div>
              <div className="text-white font-semibold text-base">Paste from Load Board</div>
              <div className="text-neutral-500 text-xs">AI reads load details from pasted text or a screenshot — DAT, Truckstop, email, SMS</div>
            </div>
          </div>
          <button
            onClick={close}
            data-testid="paste-load-close"
            className="text-neutral-500 hover:text-white p-1 transition-colors"
            aria-label="Close"
          >
            <X size={20} />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-5">
          {step === "paste" && (
            <>
              <label className="block text-xs uppercase tracking-widest text-neutral-500 mb-2">Load Posting Text</label>
              <textarea
                value={text}
                onChange={(e) => setText(e.target.value)}
                placeholder={EXAMPLE_TEXT}
                data-testid="paste-load-textarea"
                rows={12}
                className="w-full bg-black/40 border border-white/10 rounded-lg p-4 text-white text-sm placeholder:text-neutral-700 focus:outline-none focus:border-[#FF5A1F]/60 resize-y font-mono"
                autoFocus
              />
              <div className="mt-3 flex items-start gap-2 text-xs text-neutral-500">
                <Sparkle size={12} weight="fill" className="text-[#FF5A1F] shrink-0 mt-0.5" />
                <span>
                  <b className="text-neutral-300">Tip:</b> Copy the full load posting from DAT (Ctrl+A → Ctrl+C on the load detail card). Also works with broker emails, text messages, and phone-call notes.
                </span>
              </div>

              {/* Screenshot upload — DAT One mobile app doesn't allow copying text */}
              <div className="mt-5 pt-5 border-t border-white/10">
                <div className="text-xs uppercase tracking-widest text-neutral-500 mb-2">Can't copy? Upload a screenshot</div>
                <label
                  data-testid="paste-load-upload-btn"
                  className={`cursor-pointer inline-flex items-center gap-2 rounded-full border border-[#FF5A1F]/40 bg-[#FF5A1F]/10 px-4 py-2.5 text-sm font-semibold text-[#FF5A1F] hover:bg-[#FF5A1F]/20 transition-colors ${parsing ? "opacity-50 pointer-events-none" : ""}`}
                >
                  <Camera size={15} weight="fill" />
                  {parsing ? "AI reading screenshot…" : "Upload load screenshot"}
                  <input
                    type="file"
                    accept="image/png,image/jpeg,image/webp"
                    className="hidden"
                    data-testid="paste-load-file-input"
                    onChange={handleScreenshot}
                    disabled={parsing}
                  />
                </label>
                <p className="text-[11px] text-neutral-500 mt-2 leading-relaxed">
                  The DAT One app won't let you copy text — just <b className="text-neutral-300">screenshot the load</b> on your phone and upload it here. AI reads everything straight off the image.
                </p>
              </div>
            </>
          )}

          {step === "review" && parsed && (
            <>
              {/* Confidence banner */}
              <div className={`mb-4 p-3 rounded-lg border ${confPct >= 85 ? "border-emerald-500/30 bg-emerald-500/[0.05]" : confPct >= 65 ? "border-amber-500/30 bg-amber-500/[0.05]" : "border-red-500/30 bg-red-500/[0.05]"}`}>
                <div className="flex items-center gap-2 text-sm">
                  {confPct >= 85 ? (
                    <CheckCircle size={16} weight="fill" className="text-emerald-400" />
                  ) : (
                    <Warning size={16} weight="fill" className="text-amber-400" />
                  )}
                  <span className="text-white font-medium">AI confidence: <span className={confColor}>{confPct}%</span></span>
                  <span className="text-neutral-500">— Review fields below before saving</span>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs text-neutral-400 mb-1">Load # *</label>
                  <input
                    type="text"
                    value={parsed.load_number}
                    onChange={(e) => setParsed({ ...parsed, load_number: e.target.value })}
                    data-testid="paste-review-load-number"
                    className="w-full bg-black/40 border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-[#FF5A1F]/60"
                    placeholder="TQL-882741"
                  />
                </div>
                <div>
                  <label className="block text-xs text-neutral-400 mb-1">Customer / Broker *</label>
                  <input
                    type="text"
                    value={parsed.customer}
                    onChange={(e) => setParsed({ ...parsed, customer: e.target.value })}
                    data-testid="paste-review-customer"
                    className="w-full bg-black/40 border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-[#FF5A1F]/60"
                    placeholder="TQL"
                  />
                </div>
                {/* Full-width risk warning banner — silent when customer is green or unknown */}
                <div className="sm:col-span-2">
                  <PastedLoadRiskWarning customerName={parsed.customer} />
                </div>
                <div>
                  <label className="block text-xs text-neutral-400 mb-1">Origin *</label>
                  <input
                    type="text"
                    value={parsed.origin}
                    onChange={(e) => setParsed({ ...parsed, origin: e.target.value })}
                    data-testid="paste-review-origin"
                    className="w-full bg-black/40 border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-[#FF5A1F]/60"
                    placeholder="Dallas, TX"
                  />
                </div>
                <div>
                  <label className="block text-xs text-neutral-400 mb-1">Destination *</label>
                  <input
                    type="text"
                    value={parsed.destination}
                    onChange={(e) => setParsed({ ...parsed, destination: e.target.value })}
                    data-testid="paste-review-destination"
                    className="w-full bg-black/40 border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-[#FF5A1F]/60"
                    placeholder="Atlanta, GA"
                  />
                </div>
                <div>
                  <label className="block text-xs text-neutral-400 mb-1">Pickup Date *</label>
                  <input
                    type="date"
                    value={parsed.pickup_date}
                    onChange={(e) => setParsed({ ...parsed, pickup_date: e.target.value })}
                    data-testid="paste-review-pickup-date"
                    className="w-full bg-black/40 border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-[#FF5A1F]/60"
                  />
                </div>
                <div>
                  <label className="block text-xs text-neutral-400 mb-1">Delivery Date *</label>
                  <input
                    type="date"
                    value={parsed.delivery_date}
                    onChange={(e) => setParsed({ ...parsed, delivery_date: e.target.value })}
                    data-testid="paste-review-delivery-date"
                    className="w-full bg-black/40 border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-[#FF5A1F]/60"
                  />
                </div>
                <div className="sm:col-span-2">
                  <label className="block text-xs text-neutral-400 mb-1">Rate ($)</label>
                  <input
                    type="number"
                    value={parsed.rate}
                    onChange={(e) => setParsed({ ...parsed, rate: parseFloat(e.target.value) || 0 })}
                    data-testid="paste-review-rate"
                    className="w-full bg-black/40 border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-[#FF5A1F]/60 font-mono"
                    placeholder="2450"
                  />
                </div>
                <div className="sm:col-span-2">
                  <label className="block text-xs text-neutral-400 mb-1">Notes</label>
                  <textarea
                    value={parsed.notes}
                    onChange={(e) => setParsed({ ...parsed, notes: e.target.value })}
                    data-testid="paste-review-notes"
                    rows={3}
                    className="w-full bg-black/40 border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-[#FF5A1F]/60 resize-y"
                  />
                </div>
              </div>
            </>
          )}

          {step === "negotiate" && parsed && (
            <div className="space-y-5" data-testid="paste-negotiate-step">
              <div className="rounded-lg bg-emerald-500/[0.06] border border-emerald-500/25 p-3 flex items-center gap-2 text-sm">
                <CheckCircle size={16} weight="fill" className="text-emerald-400" />
                <span className="text-white">
                  Load <b className="font-mono">{parsed.load_number}</b> saved to dispatch. Now negotiate the rate 💰
                </span>
              </div>

              {/* Broker contact */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs text-neutral-400 mb-1">Broker email *</label>
                  <div className="relative">
                    <EnvelopeSimple size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-500" />
                    <input
                      type="email"
                      value={brokerEmail}
                      onChange={(e) => setBrokerEmail(e.target.value)}
                      placeholder="jake@tql.com"
                      data-testid="paste-negotiate-broker-email"
                      className="w-full bg-black/40 border border-white/10 rounded-lg text-white text-sm py-2 pl-9 pr-3 focus:outline-none focus:border-[#FF5A1F]/60"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-xs text-neutral-400 mb-1">Broker contact name</label>
                  <input
                    type="text"
                    value={brokerName}
                    onChange={(e) => setBrokerName(e.target.value)}
                    placeholder="Jake Miller (optional)"
                    data-testid="paste-negotiate-broker-name"
                    className="w-full bg-black/40 border border-white/10 rounded-lg text-white text-sm py-2 px-3 focus:outline-none focus:border-[#FF5A1F]/60"
                  />
                </div>
              </div>

              {/* Reasons */}
              <div>
                <label className="block text-xs uppercase tracking-widest text-neutral-500 mb-2">Why higher rate? (pick all that apply)</label>
                <div className="flex flex-wrap gap-2">
                  {REASONS.map((r) => {
                    const on = reasons.includes(r.key);
                    return (
                      <button
                        key={r.key}
                        type="button"
                        onClick={() => setReasons(on ? reasons.filter((k) => k !== r.key) : [...reasons, r.key])}
                        data-testid={`paste-reason-${r.key}`}
                        className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${
                          on
                            ? "border-[#FF5A1F]/50 bg-[#FF5A1F]/15 text-[#FF5A1F]"
                            : "border-white/10 text-neutral-400 hover:text-white hover:border-white/25"
                        }`}
                      >
                        {r.label}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Live signals — real EIA fuel + auto-computed deadhead miles */}
              {(fuel || deadhead) && (
                <div
                  data-testid="paste-negotiate-signals"
                  className="flex flex-wrap gap-2 pt-1"
                >
                  {fuel && (
                    <div
                      data-testid="signal-fuel-badge"
                      className="inline-flex items-center gap-2 text-xs bg-white/[0.03] border border-white/10 rounded-full pl-2 pr-3 py-1.5"
                      title={`US retail diesel avg per EIA · as of ${fuel.as_of} · source: ${fuel.source}`}
                    >
                      <span className="w-5 h-5 rounded-full bg-[#FF5A1F]/15 text-[#FF5A1F] flex items-center justify-center">
                        <Gauge size={12} weight="fill" />
                      </span>
                      <span className="text-neutral-400">Diesel</span>
                      <span className="text-white font-mono font-semibold">${Number(fuel.price_per_gallon).toFixed(3)}/gal</span>
                      {typeof fuel.wow_change_usd === "number" && fuel.wow_change_usd !== 0 && (
                        <span
                          className={
                            fuel.wow_change_usd > 0 ? "text-amber-400 font-mono" : "text-emerald-400 font-mono"
                          }
                        >
                          {fuel.wow_change_usd > 0 ? "↑" : "↓"}${Math.abs(fuel.wow_change_usd).toFixed(3)}
                        </span>
                      )}
                    </div>
                  )}
                  {deadhead && !deadhead.no_prior_load && Number(deadhead.miles) > 0 && (
                    <div
                      data-testid="signal-deadhead-badge"
                      className="inline-flex items-center gap-2 text-xs bg-white/[0.03] border border-white/10 rounded-full pl-2 pr-3 py-1.5"
                      title={`Empty miles from your last drop in ${deadhead.from} to this pickup at ${deadhead.to}`}
                    >
                      <span className="w-5 h-5 rounded-full bg-amber-500/15 text-amber-400 flex items-center justify-center">
                        <Path size={12} weight="fill" />
                      </span>
                      <span className="text-neutral-400">Deadhead</span>
                      <span className="text-white font-mono font-semibold">{Math.round(deadhead.miles)} mi</span>
                      <span className="text-neutral-500">from {deadhead.from}</span>
                    </div>
                  )}
                  {deadhead?.no_prior_load && (
                    <div
                      data-testid="signal-deadhead-empty"
                      className="inline-flex items-center gap-2 text-xs bg-white/[0.02] border border-dashed border-white/10 rounded-full px-3 py-1.5 text-neutral-500"
                    >
                      No prior delivered load — deadhead unknown
                    </div>
                  )}
                </div>
              )}

              {/* Draft (if generated) */}
              {draft ? (
                <div className="space-y-3" data-testid="paste-negotiate-draft">
                  <div className="grid grid-cols-3 gap-2 text-center text-xs">
                    <div className="rounded-lg bg-white/[0.03] border border-white/10 p-2.5">
                      <div className="text-neutral-500 uppercase tracking-wider text-[10px]">Posted</div>
                      <div className="text-white font-mono text-base mt-0.5">${draft.posted_rate.toLocaleString()}</div>
                    </div>
                    <div className="rounded-lg bg-[#FF5A1F]/[0.08] border border-[#FF5A1F]/30 p-2.5">
                      <div className="text-[#FF5A1F] uppercase tracking-wider text-[10px] font-semibold">Your Target</div>
                      <div className="text-[#FF5A1F] font-mono text-base mt-0.5 font-semibold">${draft.target_rate.toLocaleString()}</div>
                    </div>
                    <div className="rounded-lg bg-emerald-500/[0.08] border border-emerald-500/30 p-2.5">
                      <div className="text-emerald-400 uppercase tracking-wider text-[10px] font-semibold">AI Anchor</div>
                      <div className="text-emerald-400 font-mono text-base mt-0.5 font-semibold">${draft.anchor_rate.toLocaleString()}</div>
                    </div>
                  </div>
                  <div className="text-xs text-neutral-500 flex items-center gap-1.5">
                    <TrendUp size={12} weight="bold" className="text-emerald-400" />
                    <span>
                      +<b className="text-emerald-400">${draft.uplift_applied.toLocaleString()}</b> uplift over posted
                      {" "}<span className="text-neutral-600">·</span>{" "}
                      style: <span className="text-neutral-300">{draft.aggressiveness}</span>
                    </span>
                  </div>

                  {/* Risk-aware tone adjustment badge — only shown when the broker is AMBER or RED */}
                  {draft.broker_risk && (draft.broker_risk.band === "amber" || draft.broker_risk.band === "red") && (
                    <div
                      data-testid={`negotiate-tone-badge-${draft.broker_risk.band}`}
                      className={`rounded-lg border p-3 ${
                        draft.broker_risk.band === "red"
                          ? "border-rose-500/40 bg-rose-500/10"
                          : "border-amber-500/40 bg-amber-500/10"
                      }`}
                    >
                      <div className={`flex items-center gap-1.5 text-[10px] uppercase tracking-widest font-bold ${
                        draft.broker_risk.band === "red" ? "text-rose-300" : "text-amber-300"
                      }`}>
                        <Sparkle size={11} weight="fill" />
                        Tone auto-adjusted for {draft.broker_risk.band === "red" ? "HIGH-RISK" : "SLOW-PAYING"} broker
                      </div>
                      <div className={`text-xs mt-1 ${
                        draft.broker_risk.band === "red" ? "text-rose-100/90" : "text-amber-100/90"
                      }`}>
                        {draft.broker_risk.band === "red" ? (
                          <>
                            Draft is tight, professional, and tactfully proposes tying this rate confirmation
                            to a payment commitment on the{" "}
                            <b>${(draft.broker_risk.total_outstanding || 0).toLocaleString()}</b> outstanding.
                          </>
                        ) : (
                          <>
                            Draft stays warm but subtly reinforces on-time payment — the broker has slower
                            payment history{draft.broker_risk.avg_days_to_pay ? ` (avg ${Math.round(draft.broker_risk.avg_days_to_pay)}d late)` : ""}.
                          </>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Rejection-learning badge — AI factored the broker's prior rejections into the anchor */}
                  {draft.rejection_history && (draft.rejection_history.rejection_count || 0) >= 2 && (
                    <div
                      data-testid="negotiate-rejection-learning-badge"
                      className="rounded-lg border border-indigo-500/40 bg-indigo-500/10 p-3"
                    >
                      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-widest font-bold text-indigo-300">
                        <Sparkle size={11} weight="fill" />
                        AI Learned From {draft.rejection_history.rejection_count} Prior Rejection{draft.rejection_history.rejection_count === 1 ? "" : "s"}
                      </div>
                      <div className="text-xs mt-1 text-indigo-100/90">
                        Anchoring near this broker's historical ceiling of{" "}
                        <b>${Number(draft.rejection_history.typical_ceiling_per_mile || 0).toFixed(2)}/mi</b>
                        {draft.rejection_history.no_response_count > 0 && (
                          <> · <span className="text-indigo-200/80">{draft.rejection_history.no_response_count} ghosted</span></>
                        )}
                        {(draft.rejection_history.same_lane_rejection_count || 0) >= 2 && draft.rejection_history.same_lane_avg_ask_per_mile && (
                          <> · Same-lane avg <b>${Number(draft.rejection_history.same_lane_avg_ask_per_mile).toFixed(2)}/mi</b></>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Wins-anchor badge — AI factored the broker's proven willingness-to-pay floor */}
                  {draft.wins_history && (draft.wins_history.win_count || 0) >= 1 && (
                    <div
                      data-testid="negotiate-wins-anchor-badge"
                      className="rounded-lg border border-emerald-500/40 bg-emerald-500/10 p-3"
                    >
                      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-widest font-bold text-emerald-300">
                        <Sparkle size={11} weight="fill" />
                        AI Learned From {draft.wins_history.win_count} Prior Win{draft.wins_history.win_count === 1 ? "" : "s"}
                      </div>
                      <div className="text-xs mt-1 text-emerald-100/90">
                        {draft.wins_history.win_count >= 2 ? (
                          <>Proven floor <b>${Number(draft.wins_history.confident_floor_per_mile || 0).toFixed(2)}/mi</b></>
                        ) : (
                          <>Won at <b>${Number(draft.wins_history.avg_won_per_mile || 0).toFixed(2)}/mi</b></>
                        )}
                        {" · Best "}<b>${Number(draft.wins_history.best_won_per_mile || 0).toFixed(2)}/mi</b>
                        {(draft.wins_history.same_lane_win_count || 0) >= 1 && draft.wins_history.same_lane_avg_won_per_mile && (
                          <> · Same-lane avg <b>${Number(draft.wins_history.same_lane_avg_won_per_mile).toFixed(2)}/mi</b></>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Rate Card badge — AI grounded the reply in the owner's real
                      (customer × lane) history. Silent when this is a first-time lane. */}
                  {draft.rate_card && (
                    <div
                      data-testid="negotiate-rate-card-badge"
                      className="rounded-lg border border-violet-500/40 bg-violet-500/10 p-3"
                    >
                      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-widest font-bold text-violet-300">
                        <Sparkle size={11} weight="fill" />
                        Grounded on your rate card
                      </div>
                      <div className="text-xs mt-1 text-violet-100/90 leading-relaxed">
                        <b>{draft.rate_card.loads_delivered} loads</b> for {draft.rate_card.customer} on{" "}
                        <b className="font-mono">{draft.rate_card.lane}</b> at avg{" "}
                        <b>${Number(draft.rate_card.rate_avg).toLocaleString()}</b>
                        {draft.rate_card.on_time_pct != null && (
                          <> · <b>{Math.round(draft.rate_card.on_time_pct)}%</b> on-time</>
                        )}
                        . Counter range{" "}
                        <b className="text-white">
                          ${Number(draft.rate_card.counter_offer?.low || 0).toLocaleString()}–
                          ${Number(draft.rate_card.counter_offer?.high || 0).toLocaleString()}
                        </b>
                        .
                      </div>
                    </div>
                  )}
                  <div>
                    <label className="block text-xs text-neutral-400 mb-1">Subject</label>
                    <input
                      type="text"
                      value={draft.subject}
                      onChange={(e) => setDraft({ ...draft, subject: e.target.value })}
                      data-testid="paste-negotiate-subject"
                      className="w-full bg-black/40 border border-white/10 rounded-lg text-white text-sm py-2 px-3 focus:outline-none focus:border-[#FF5A1F]/60"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-neutral-400 mb-1">Email body (edit before sending)</label>
                    <textarea
                      value={draft.body}
                      onChange={(e) => setDraft({ ...draft, body: e.target.value })}
                      data-testid="paste-negotiate-body"
                      rows={12}
                      className="w-full bg-black/40 border border-white/10 rounded-lg text-white text-sm p-3 focus:outline-none focus:border-[#FF5A1F]/60 resize-y font-mono leading-relaxed"
                    />
                  </div>
                </div>
              ) : (
                <div className="rounded-lg border border-dashed border-white/10 bg-white/[0.02] p-5 text-center">
                  <Sparkle size={20} weight="fill" className="text-[#FF5A1F] mx-auto mb-2" />
                  <div className="text-white font-medium text-sm">Ready to draft your negotiation?</div>
                  <p className="text-neutral-500 text-xs mt-1">
                    AI will use your pricing rules to write a professional, data-backed email.
                  </p>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-5 border-t border-white/10 gap-3">
          {step === "paste" && (
            <>
              <button
                onClick={close}
                data-testid="paste-load-cancel"
                className="text-neutral-400 hover:text-white text-sm px-3 py-2 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleParse}
                disabled={parsing || text.trim().length < 10}
                data-testid="paste-load-parse-btn"
                className="rounded-full px-5 py-2.5 text-sm font-semibold bg-[#FF5A1F] text-black hover:bg-[#FF7038] disabled:opacity-50 disabled:cursor-not-allowed transition-colors inline-flex items-center gap-2"
              >
                {parsing ? (
                  <><Sparkle size={13} weight="fill" className="animate-pulse" /> AI parsing...</>
                ) : (
                  <><Sparkle size={13} weight="fill" /> Parse with AI <ArrowRight size={13} weight="bold" /></>
                )}
              </button>
            </>
          )}
          {step === "review" && (
            <>
              <button
                onClick={() => { setStep("paste"); setParsed(null); }}
                data-testid="paste-load-back-btn"
                className="text-neutral-400 hover:text-white text-sm px-3 py-2 transition-colors"
              >
                ← Back to paste
              </button>
              <button
                onClick={handleSave}
                disabled={saving}
                data-testid="paste-load-save-btn"
                className="rounded-full px-5 py-2.5 text-sm font-semibold bg-[#FF5A1F] text-black hover:bg-[#FF7038] disabled:opacity-50 transition-colors inline-flex items-center gap-2"
              >
                {saving ? "Saving..." : <>Save & Negotiate <ArrowRight size={13} weight="bold" /></>}
              </button>
            </>
          )}
          {step === "negotiate" && (
            <>
              <button
                onClick={close}
                data-testid="paste-negotiate-skip"
                className="text-neutral-400 hover:text-white text-sm px-3 py-2 transition-colors"
              >
                Skip — I'll email later
              </button>
              {draft ? (
                <div className="flex gap-2">
                  <button
                    onClick={() => setDraft(null)}
                    className="text-neutral-400 hover:text-white text-xs px-3 py-2 transition-colors"
                    data-testid="paste-negotiate-redraft"
                  >
                    Re-draft
                  </button>
                  <button
                    onClick={handleSendEmail}
                    disabled={sending}
                    data-testid="paste-negotiate-send-btn"
                    className="rounded-full px-5 py-2.5 text-sm font-semibold bg-[#FF5A1F] text-black hover:bg-[#FF7038] disabled:opacity-50 transition-colors inline-flex items-center gap-2"
                  >
                    {sending ? "Sending..." : (<><PaperPlaneTilt size={13} weight="fill" /> Send to broker</>)}
                  </button>
                </div>
              ) : (
                <button
                  onClick={handleDraftEmail}
                  disabled={drafting || !brokerEmail}
                  data-testid="paste-negotiate-draft-btn"
                  className="rounded-full px-5 py-2.5 text-sm font-semibold bg-[#FF5A1F] text-black hover:bg-[#FF7038] disabled:opacity-50 transition-colors inline-flex items-center gap-2"
                >
                  {drafting ? (
                    <><Sparkle size={13} weight="fill" className="animate-pulse" /> AI drafting...</>
                  ) : (
                    <><Sparkle size={13} weight="fill" /> Draft with AI <ArrowRight size={13} weight="bold" /></>
                  )}
                </button>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
